import { caTypes } from '@mca/contentapi-js';
import { getUser } from '../services/user';
import { getKeyValues, getGvpData } from '../services/config';
import { streamingTypes, quality as qualityCode } from '../constants/types';

/**
 * Returns the media with the highest quality of a given array of medias.
 * HBO -> Android sends 64, but iOS sends 4.
 * @param {Array} medias Medias.
 * @param {boolean} is4K 4K quality.
 * @returns {Object} Media with the best quality.
 */
export function getBestMedia(medias, is4K) {
  return medias
    .filter(
      ({ playType }) =>
        playType === caTypes.STREAMING_TYPE.DashWidevine ||
        playType === streamingTypes.DASH_H264 ||
        (is4K && playType === streamingTypes.DASH_H265)
    )
    .filter(({ quality }) =>
      quality >= is4K ? qualityCode.HighDefinitionFull : qualityCode.HighDefinitionFull + 1
    )
    .sort((a, b) => {
      // Greater number implies less quality
      return a.quality - b.quality;
    })[0];
}

/**
 * Get quality based on code.
 * @param {number} Quality code.
 * @return {string} Quality name.
 */
export function getQuality(qualityId) {
  var quality;
  switch (qualityId) {
    case 1:
    case 2:
      quality = 'HD';
      break;
    case 3:
      quality = 'SD';
      break;
    case 4:
      quality = 'LD';
      break;
  }
  return quality;
}

/**
 * Replaces placeholder from the media URL with the user playback id.
 * @param {string} media URL to replace its placeholder.
 * @returns {string} Media URL replaced.
 */
export function replaceUserPlaceholder(media) {
  const playbackId = getUser().playbackId;
  const urlPlaceholder = getKeyValues().urlPlaceholder;
  return urlPlaceholder && playbackId ? media.replace(urlPlaceholder, playbackId) : media;
}

/**
 * Replaces macro placeholders from the media URL with device values.
 * @param {string} media URL to replace its placeholder.
 * @param {boolean} isLive Live content playing.
 * @param {boolean} isReplacement URL must be replaced.
 * @returns {string} Media URL replaced.
 */
export function replaceMacrosPlaceholders(media, isLive, isReplacement) {
  if (isReplacement && (isLive ? getKeyValues().isMacrosEnabled : getKeyValues().isAvodEnabled)) {
    const FAST_CHANNELS_MACROS = {
      ua: window.navigator.userAgent,
      env: getKeyValues().environmentTCS,
      app_bundle: getKeyValues().appBuncleTCS,
      app_store_url: getKeyValues().appStoreUrlTCS,
      app_name: getKeyValues().appNameTCS,
      domain: getKeyValues().domainTCS,
      ifa: cast.framework.CastReceiverContext.getInstance().getApplicationData().sessionId,
      ifa_type: getKeyValues().ifaTypeTCS,
      did: getGvpData().deviceId,
      device_make: 'Chromecast',
      device_model: 'Chromecast'
    };
    const originalUrl = new URL(media);
    const replacedlUrl = new URL(media);
    originalUrl.searchParams.forEach((value, key) => {
      Object.prototype.hasOwnProperty.call(FAST_CHANNELS_MACROS, key) &&
        replacedlUrl.searchParams.set(key, FAST_CHANNELS_MACROS[key]);
    });
    return replacedlUrl.toString();
  } else {
    return media;
  }
}

/**
 * Gets new URL with a unique profile applied.
 * @param {string} url URL of media.
 * @param {number} quality Quality of media.
 * @returns {string} URL with unique profile applied.
 */
export function getUniqueProfile(url, quality) {
  const tcdnFilterName = getKeyValues().tcdnFilterName;
  const isUniqueProfile = url.includes(tcdnFilterName);
  if (getKeyValues().isUniqueProfileEnabled && tcdnFilterName && isUniqueProfile) {
    return url.replace(
      new RegExp(`${tcdnFilterName}=[^&]*`),
      `${tcdnFilterName}=${getTcdnQuality(quality)}`
    );
  } else {
    return url;
  }
}

/**
 * Gets TCDN value.
 * @param {number} quality Quality of media.
 * @returns {string} TCDN value.
 */
function getTcdnQuality(quality) {
  switch (quality) {
    case qualityCode.LowDefinition:
      return getKeyValues().tcdnSUBSD;
    case qualityCode.StandardDefinition:
      return getKeyValues().tcdnSD;
    case qualityCode.HighDefinition:
      return getKeyValues().tcdnHD;
    default:
      return '';
  }
}
